import { useEffect, useState } from 'react';
import { companyInfo } from '@/mocks/home';
import { submitMessage } from '@/api/message';
import { getProductTypeDict, getMessageTypeDict, type DictData } from '@/api/dict';

const emptyForm = { userName: '', companyName: '', contactInfo: '', productType: '', messageType: '', messageContent: '' };

function ConsultationForm() {
  const [formData, setFormData] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [productOptions, setProductOptions] = useState<DictData[]>([]);
  const [messageOptions, setMessageOptions] = useState<DictData[]>([]);

  useEffect(() => {
    getProductTypeDict().then((res) => setProductOptions(res.data ?? [])).catch(() => {});
    getMessageTypeDict().then((res) => setMessageOptions(res.data ?? [])).catch(() => {});
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitMessage(formData);
      setSubmitted(true);
      setFormData(emptyForm);
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      alert(err instanceof Error ? err.message : '提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-2">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-medium text-primary-600 bg-primary-50 rounded-full">
              联系我们
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              开启 AI 翻译新体验
            </h2>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-8">
              无论您是政企批量采购、企业定制开发，还是个人选购，我们的专业团队都将为您提供一对一咨询服务。
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 flex-shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">客服热线</div>
                  <a href="tel:400-097-5066" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                    {companyInfo.serviceHotline}
                  </a>
                  <span className="text-xs text-foreground-400 ml-2">({companyInfo.serviceTime})</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">商务合作</div>
                  <a href={`mailto:${companyInfo.marketingEmail}`} className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                    {companyInfo.marketingEmail}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">公司地址</div>
                  <div className="text-sm text-foreground-500 leading-relaxed">{companyInfo.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-primary-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-500 mb-1">感谢您的咨询，我们的商务团队将在</p>
                  <p className="text-sm font-semibold text-primary-600">1个工作日内与您联系</p>
                </div>
              ) : (
                <form
                  id="consultation-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d8fs2i43dgjb0046nd1g"
                  method="POST"
                  onSubmit={handleSubmit}
                >
                  <h3 className="text-lg font-bold text-foreground-900 mb-6">在线咨询</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="userName"
                        value={formData.userName}
                        onChange={handleChange}
                        required
                        placeholder="请输入您的姓名"
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        单位名称
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="请输入单位名称"
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        联系方式 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="contactInfo"
                        value={formData.contactInfo}
                        onChange={handleChange}
                        required
                        placeholder="手机号或邮箱"
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        需求产品 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="productType"
                        value={formData.productType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all appearance-none cursor-pointer"
                        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M6 8L1 3h10z' fill='%23999'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      >
                        <option value="">请选择需求产品</option>
                        {productOptions.map((opt) => (
                          <option key={opt.dictValue} value={opt.dictValue}>
                            {opt.dictLabel}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        咨询类型 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="messageType"
                        value={formData.messageType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all appearance-none cursor-pointer"
                        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M6 8L1 3h10z' fill='%23999'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      >
                        <option value="">请选择咨询类型</option>
                        {messageOptions.map((opt) => (
                          <option key={opt.dictValue} value={opt.dictValue}>
                            {opt.dictLabel}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      name="messageContent"
                      value={formData.messageContent}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      placeholder="请描述您的具体需求，以便我们提供更精准的服务..."
                      className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all resize-none"
                    />
                    <div className="text-xs text-foreground-400 mt-1 text-right">{formData.messageContent.length}/500</div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:bg-primary-600 transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? '提交中...' : '提交咨询'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ConsultationForm;